import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  Button,
  Text,
  Flex,
  IconButton,
  Box,
} from "@chakra-ui/react";
import { AiOutlineComment } from "react-icons/ai";
import { Alert, Button as BtnUI, Spinner } from "../UI";
import { ReplayIcon } from "../Icons";
import MessageItem from "./MessagesItem";
import {
  useProgramMessages,
  useInvalidateProgramMessages,
} from "../../hooks/ProgramMessages";
import WithoutResults from "../WithoutResults";
import { useNotificationPreferences } from "../../hooks/Users";
import {
  canConfigureNotificationPreferences,
  getNotificationMode,
} from "../../Utils/NotificationPreferences";
import { mergeMessagesPage } from "./messagesState";

const MessagesContainer = () => {
  const [page, setPage] = useState(0);
  const [alertsAccumlated, setAlertsAccumlated] = useState([]);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const { data, isLoading, isFetching, isError, refetch } =
    useProgramMessages(page);
  const { user } = useSelector((state) => state.auth);
  const { programId, type } = useParams();
  const { data: notificationPreferences } = useNotificationPreferences(
    canConfigureNotificationPreferences(user)
  );
  const notificationMode = getNotificationMode(
    notificationPreferences || user,
    user
  );
  useInvalidateProgramMessages({
    user,
    setPage,
    setHasNewMessages,
    programId,
    type,
    notificationMode,
  });

  useEffect(() => {
    const currentMessages = data?.alerts?.currentAlerts;
    if (!Array.isArray(currentMessages)) return;

    setAlertsAccumlated((previousMessages) =>
      mergeMessagesPage(previousMessages, currentMessages, page)
    );
  }, [data, page]);

  return (
    <Menu>
      <MenuButton
        as={Button}
        bg={"white"}
        _active={{ bg: "white" }}
        _hover={{ bg: "white" }}
        position="relative"
        onClick={() => setHasNewMessages(false)}
      >
        <IconButton
          fontSize={"2xl"}
          icon={<AiOutlineComment />}
          variant="ghost"
          color="gray.600"
        />
        {hasNewMessages && (
          <Box
            position="absolute"
            top={0}
            right={1}
            width={4}
            height={4}
            bg="red.500"
            borderRadius="full"
            border="2px solid white"
            zIndex={1}
          />
        )}
      </MenuButton>
      <MenuList
        w={{
          lg: "xl",
          md: "md",
          sm: "sm",
        }}
        mr={{
          lg: 0,
          md: 5,
          sm: 5,
        }}
        borderRadius={12}
        border={"1px dotted #82B378"}
        boxShadow={"2xl"}
        p={4}
        zIndex={999}
      >
        <Flex justify={"space-between"}>
          <Text
            noOfLines={1}
            fontWeight={700}
            color={"black"}
            fontSize={"xl"}
            mb={4}
          >{`Notificaciones`}</Text>
          <Flex gap={2}>
            {(isLoading || isFetching) && <Spinner size={"md"} show={true} />}
            <Icon
              fontSize={"2xl"}
              as={ReplayIcon}
              color={"green.100"}
              _hover={{ color: "green.500", cursor: "pointer" }}
              onClick={refetch}
            />
          </Flex>
        </Flex>

        {isError && (
          <Flex w={"100%"} align={"center"} justify={"center"} py={3}>
            <Alert
              status={"error"}
              message={"Error al Cargar las notificaciones"}
            />
          </Flex>
        )}
        <Flex
          direction={"column"}
          maxH={{
            lg: "lg",
            md: "md",
            sm: "sm",
          }}
          overflowY={"auto"}
          css={{
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              width: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#2E7423",
              borderRadius: "30px",
            },
          }}
        >
          {user && (
            <>
              {alertsAccumlated.length > 0 ? (
                <>
                  {alertsAccumlated.map((item) => (
                    <MenuItem
                      minH="48px"
                      key={item?._id}
                      _focus={{ bg: "white" }}
                      borderBottom={"1px solid #D5D5D5"}
                    >
                      <MessageItem alert={item} user={user} />
                    </MenuItem>
                  ))}
                  <Flex justify={"center"} align={"center"} mt={4}>
                    <BtnUI
                      text={"Ver más"}
                      bg={"grey.100"}
                      bghover={"grey.500"}
                      color={"black"}
                      onClick={() => setPage(page + 1)}
                      disabled={
                        alertsAccumlated.length >= data?.alerts?.totalAlerts ||
                        data?.alerts?.currentAlerts?.length === 0 ||
                        isLoading ||
                        isFetching
                      }
                      isLoading={isLoading || isFetching}
                    />
                  </Flex>
                </>
              ) : (
                <WithoutResults type={"ALERTS"} />
              )}
            </>
          )}
        </Flex>
      </MenuList>
    </Menu>
  );
};

export default MessagesContainer;
